import { COLOR_FILTERS_STATUS } from '@ipfabric/utils/api/Reports';
import {
  type ReportSeverity,
  type ReportSeverityResults,
  reportSeverities,
} from '@ipfabric/utils/report';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import type { FC } from 'react';
import { Icon } from '../Icon/Icon';
import { Link } from '../Link/Link';
import { IntentResultBadge } from './IntentResultBadge';
import {
  intentResultBadgeVariants,
  intentVerificationPanelVariants,
} from './IntentVerification.styles';
import type { IntentVerificationPanelProps } from './IntentVerification.types';
import { IntentVerificationTable } from './IntentVerificationTable';

/**
 * IntentVerificationPanel displays a collapsible panel with intent verification rules.
 * Shows a summary of severity counts and allows expanding to see/edit individual rules.
 */
export const IntentVerificationPanel: FC<IntentVerificationPanelProps> = (
  props,
) => {
  const [expanded, setExpanded] = useState(false);
  const styles = intentVerificationPanelVariants({ expanded });

  const { severitySummary, loadingSeverities } = useMemo<{
    severitySummary: Partial<ReportSeverityResults>;
    loadingSeverities: ReportSeverity[];
  }>(() => {
    if (!props.data || props.data.length === 0) {
      return { severitySummary: {}, loadingSeverities: [] };
    }

    const loadingSet = new Set<ReportSeverity>();

    const summary = props.data.reduce<Partial<ReportSeverityResults>>(
      (acc, d) => {
        for (const severity of reportSeverities) {
          const count = d.result?.checks?.[severity];

          if (d.status === COLOR_FILTERS_STATUS.run) {
            loadingSet.add(severity);
            continue;
          }

          if (count !== undefined && acc[severity] !== null) {
            acc[severity] = (acc[severity] ?? 0) + count;
          }
        }

        return acc;
      },
      {},
    );

    return {
      severitySummary: summary,
      loadingSeverities: [...loadingSet],
    };
  }, [props.data]);

  return (
    <div className={classNames(styles.container(), props.className)}>
      <div className='rounded-lg shadow mb-4 bg-default overflow-hidden max-w-full'>
        <div
          className={styles.panel()}
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          <div className={styles.titleSection()}>
            <div className={styles.summaryTitle()}>
              Intent verification rules
            </div>
            <Link
              size='default'
              iconPosition='start'
              icon='fa-plus'
              renderAs='button'
              onClick={(e) => {
                e.stopPropagation();
                props.onAddRuleClick?.();
              }}
            >
              Add
            </Link>
          </div>
          <div className={styles.summarySection()}>
            <div className={styles.summaryLabel()}>Results summary:</div>
            <div className={styles.summaryBadges()}>
              {reportSeverities.map((severity) => {
                const isLoading = loadingSeverities.includes(severity);
                const hasCount = severitySummary[severity] !== undefined;

                if (hasCount || isLoading) {
                  return (
                    <IntentResultBadge
                      key={severity}
                      severity={severity}
                      count={severitySummary[severity] ?? undefined}
                      status={isLoading ? COLOR_FILTERS_STATUS.run : undefined}
                    />
                  );
                }

                return (
                  <div key={severity} className={intentResultBadgeVariants()} />
                );
              })}
            </div>
          </div>
          <div className={styles.chevronIcon()}>
            <Icon glyph={expanded ? 'fa-angle-up' : 'fa-angle-down'} />
          </div>
        </div>
        <div className={styles.tableWrapper()}>
          <IntentVerificationTable
            data={props.data}
            intentFilters={props.intentFilters}
            onDeleteClick={props.onDeleteClick}
            onEditClick={props.onEditClick}
            onSeverityClick={props.onSeverityClick}
          />
        </div>
      </div>
    </div>
  );
};
